import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { BaseForm } from "app/data/shared/models/base-form.interface";
import { ApiService } from "app/modules/shared/services/base/api.service";
import { TranslateService } from "app/modules/shared/services/translate/translate.service";
import * as wjGrid from "wijmo/wijmo.grid";
import * as wjCore from "wijmo/wijmo";
import { ToastService } from "app/modules/shared/services/message/toast.service";
import { BehaviorSubject, of, forkJoin } from "rxjs";
import { KlLoadingService } from "app/modules/shared/services/kl-loading.service";
import { ConvertIsoDateService } from "app/modules/shared/services/convert-iso-date.service";
import { UtilService } from "app/modules/shared/services/util.service";
import { ExplorerService } from "app/modules/shared/services/explorer.service";
import { PrimengLocal } from "app/modules/shared/constants/primeng-local";
import { DocumentTypesEnum } from "@app/modules/shared/enum/document-types.enum";
import { NumberSchemaDto } from "@dashboard/modules/application-params/number-schema/model/number-schema.dto";
import { catchError } from "rxjs/operators";

@Component({
  selector: "kl-number-schema-form",
  templateUrl: "./number-schema-form.component.html",
  styleUrls: ["./number-schema-form.component.scss"],
})
export class NumberSchemaFormComponent implements OnInit, BaseForm, OnDestroy {
  @ViewChild("numberSchemaForm", { static: true }) numberSchemaForm;
  @Input() entity: NumberSchemaDto = new NumberSchemaDto();
  @Input() tabId: string;
  @Input() listTabId: string;
  @Input() modalId: string;
  locale =
    PrimengLocal[
      localStorage.getItem("currentLanguage")
        ? localStorage.getItem("currentLanguage").toUpperCase()
        : "TR"
    ];
  alertShow: boolean;
  alertType: string;
  alertMessage: any;
  dateError: boolean;
  isOpenLine = false;
  showLineGrid = false;
  mode = "add";
  title = "";
  schemeCurrentLength = 2;
  maxLength = 20;
  schemeLength = [2];
  isReadOnly: boolean;
  dateFormatString: string;
  endDateMin = new Date();
  numberSchemeDto = {
    rec_status: "NEW",
    type: "FIXED",
    length: 2,
    value: "",
  };
  documentTypeOptions = [
    {
      label: this.translateService.instant(DocumentTypesEnum.VIRTUAL_ORDER),
      value: DocumentTypesEnum.VIRTUAL_ORDER,
    },
  ];

  numberSchemeTypeSource = [
    { type: "FIXED", desc: this.translateService.instant("FIXED") },
    { type: "INCREMENTAL", desc: this.translateService.instant("INCREMENTAL") },
    {
      type: "GROUPEDINCREMENTAL",
      desc: this.translateService.instant("GROUPEDINCREMENTAL"),
    },
    { type: "YEAR", desc: this.translateService.instant("YEAR") },
    { type: "MONTH", desc: this.translateService.instant("MONTH") },
    { type: "WEEK", desc: this.translateService.instant("WEEK") },
    { type: "DAY", desc: this.translateService.instant("DAY") },
    { type: "USERCODE", desc: this.translateService.instant("USERCODE") },
  ];
  numberSchemeTypeDataMap: wjGrid.DataMap;
  numberSchemeUserRoleArray: Array<any>;
  numberSchemeUserArray: Array<any>;
  forkJoinStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  numberSchemeCollectionView: wjCore.CollectionView;
  numberSchemeDetailOptions = {
    getError: (item: any, property: any) => {
      if (property === "length") {
        if (item.length === "") {
          return this.translateService.instant("cant_blank_length");
        }
      }
    },
  };
  // validation vars
  showValidationErrors = true;
  validateEdits = true;

  constructor(
    private apiService: ApiService,
    private translateService: TranslateService,
    private dateService: ConvertIsoDateService,
    private util: UtilService,
    private toastService: ToastService,
    private klLoadingService: KlLoadingService,
    private explorerService: ExplorerService
  ) {
    // define the dataMaps...
    this.dateFormatString = UtilService.dateFormatString
      .toLowerCase()
      .replace("yy", "");
    this.numberSchemeTypeDataMap = new wjGrid.DataMap(
      this.numberSchemeTypeSource,
      "type",
      "desc"
    );
    // this.numberSchemePropertyArray = new wjGrid.DataMap([
    //   {property: 'BRANCHNUM', desc: translateService.instant('BRANCHNUM')},
    //   {property: 'WAREHOUSENUM', desc: translateService.instant('WAREHOUSENUM')},
    //   {property: 'USERCODE', desc: translateService.instant('USERCODE')},
    //   {property: 'YEAR', desc: translateService.instant('YEAR')},
    // ], 'property', 'desc');
    // load dropdowns
    this.klLoadingService.show("loading");
  }

  ngOnInit() {
    const getRoles = this.apiService
      .getFilteredData("role", { filter: { status: "ACTIVE" } })
      .pipe(catchError((error: any) => this.handleError(error)));
    const getUsers = this.apiService
      .getFilteredData("user", { filter: { status: "ACTIVE" } })
      .pipe(catchError((error: any) => this.handleError(error)));
    forkJoin([getRoles, getUsers]).subscribe(([role, user]) => {
      this.numberSchemeUserRoleArray = role ? (role.data ? role.data : []) : [];
      this.numberSchemeUserArray = user ? (user.data ? user.data : []) : [];
      this.forkJoinStatus.next(true);
      this.initDefaults();
    });
    switch (this.mode) {
      case "add":
        this.entity.begin_date = new Date();
        this.entity.end_date = new Date();
        this.entity.document_type = this.documentTypeOptions[0].value;
        this.entity.status = "ACTIVE";
        this.entity.number_scheme_lines = [];
        this.entity.number_scheme_lines.push(this.numberSchemeDto);
        this.title =
          this.translateService.instant("new") +
          " " +
          this.translateService.instant("number_schema");
        break;
      case "view":
      case "edit":
        this.entity.begin_date = new Date(this.entity.begin_date);
        this.entity.end_date = new Date(this.entity.end_date);
        this.title = this.translateService.instant("number_schema") + " - ";
        break;
    }
    this.numberSchemeCollectionView = new wjCore.CollectionView(
      this.entity.number_scheme_lines,
      this.numberSchemeDetailOptions
    );
    this.numberSchemeCollectionView.trackChanges = true;
  }

  openLineAccordion(event) {
    this.isOpenLine = event;
    if (!this.showLineGrid) {
      this.showLineGrid = true;
    }
  }

  formChange(event) {
    if (this.tabId) {
      this.explorerService.changeFormStatus({
        tabId: this.tabId,
        changeForm: event,
      });
    }
  }

  addNewRow() {
    if (this.schemeCurrentLength < this.maxLength) {
      this.formChange(true);
      if (this.maxLength - this.schemeCurrentLength >= 2) {
        this.entity.number_scheme_lines.push({
          type: this.numberSchemeTypeSource[0].type || "FIXED",
          length: 2,
          value: "",
        });
        this.schemeLength.push(2);
      } else {
        this.entity.number_scheme_lines.push({
          type: this.numberSchemeTypeSource[0].type || "FIXED",
          length: 1,
          value: "",
        });
        this.schemeLength.push(1);
      }
    }
    this.calculating();
  }

  cellEdit(editData) {
    this.formChange(true);
    const col = editData.flexGrid.columns[editData.event.col];
    if (col.binding === "value") {
      col.maxLength =
        editData.flexGrid.rows[editData.event.row].dataItem.length;
    }
  }

  enableCustomValidation(event) {
    this.formChange(true);
    const col = event.flexGrid.columns[event.col];
    const row = event.row;
    const dataItem = event.flexGrid.rows[row].dataItem;
    this.numberSchemeCollectionView.beginUpdate();
    this.numberSchemeCollectionView.editItem(dataItem);
    if (dataItem.id) {
      if (dataItem.hasOwnProperty("rec_status")) {
        dataItem.rec_status = "EDIT";
      } else {
        Object.assign(dataItem, { rec_status: "EDIT" });
      }
    }
    if (col.binding.includes("length")) {
      if (dataItem.length === 0) {
        dataItem.length = 1;
      } else if (
        dataItem.length - this.schemeLength[row] + this.schemeCurrentLength >=
        this.maxLength
      ) {
        if (this.schemeCurrentLength < this.maxLength) {
          dataItem.length =
            this.maxLength + this.schemeLength[row] - this.schemeCurrentLength;
        } else if (this.schemeCurrentLength === this.maxLength) {
          dataItem.length = this.schemeLength[row];
        } else {
          this.deleteCell(dataItem, row);
        }
      }
      this.schemeLength[row] = dataItem.length;
    }
    if (col.binding.includes("type")) {
      let notFindType = true;
      this.numberSchemeTypeSource.forEach((type) => {
        if (dataItem.type === type.type) {
          notFindType = false;
        }
      });
      if (notFindType) {
        dataItem.type = UtilService.deepCopy(
          this.numberSchemeTypeSource[0].type
        );
      }
    }
    this.numberSchemeCollectionView.commitEdit();
    this.numberSchemeCollectionView.endUpdate();
    this.calculating();
  }

  calculateSchemeLength(event) {
    this.formChange(true);
    this.schemeLength.splice(event.row, 1);
    this.calculating();
  }

  deleteCell(cell, row) {
    this.numberSchemeCollectionView.remove(cell);
    this.schemeLength.splice(row, 1);
    this.calculating();
  }

  calculating() {
    this.schemeCurrentLength = 0;
    for (
      let i = 0;
      i < this.numberSchemeCollectionView.sourceCollection.length;
      i++
    ) {
      this.schemeCurrentLength =
        this.schemeCurrentLength +
        this.numberSchemeCollectionView.sourceCollection[i].length;
    }
    this.numberSchemeCollectionView.refresh();
  }

  clearForm(): void {
    this.alertShow = false;
    Object.keys(this.entity).forEach((key) => {
      delete this.entity[key];
    });
    this.entity.number_scheme_lines = [];
    this.entity.number_scheme_lines.push(this.numberSchemeDto);
    this.numberSchemeCollectionView = new wjCore.CollectionView(
      this.entity.number_scheme_lines,
      this.numberSchemeDetailOptions
    );
    this.entity.status = "ACTIVE";
    this.entity.document_type = "PURCHASE_INVOICE";
    this.entity.begin_date = new Date();
    this.entity.end_date = new Date();
    this.schemeCurrentLength = 2;
    this.schemeLength = [2];
  }

  commitSave(action: any): void {
    if (this.entity.end_date < this.entity.begin_date) {
      this.dateError = true;
    } else {
      if (this.numberSchemaForm.valid) {
        this.klLoadingService.show("saving");
        if (this.entity.user_role) {
          this.entity.user_role = {
            id: this.entity.user_role.id,
          };
        }
        if (this.entity.user) {
          this.entity.user = {
            id: this.entity.user.id,
          };
        }
        if (this.numberSchemeCollectionView.itemsRemoved.length > 0) {
          for (
            let i = 0;
            i < this.numberSchemeCollectionView.itemsRemoved.length;
            i++
          ) {
            if (this.numberSchemeCollectionView.itemsRemoved[i].id) {
              if (this.numberSchemeCollectionView.itemsRemoved[i].rec_status) {
                this.numberSchemeCollectionView.itemsRemoved[i].rec_status =
                  "DELETE";
              } else {
                Object.assign(this.numberSchemeCollectionView.itemsRemoved[i], {
                  rec_status: "DELETE",
                });
              }
              this.entity.number_scheme_lines.push(
                this.numberSchemeCollectionView.itemsRemoved[i]
              );
            }
          }
        }
        switch (action) {
          case "SAVE":
            {
              switch (this.mode) {
                case "add": {
                  this.apiService
                    .addData("numberscheme", this.entity)
                    .subscribe(
                      (response) => this.handleResponse(response),
                      (error) => this.handleError(error, true)
                    );
                  break;
                }
                case "edit": {
                  this.apiService
                    .updateData("numberscheme", this.entity)
                    .subscribe(
                      (response) => this.handleResponse(response),
                      (error) => this.handleError(error, true)
                    );
                  break;
                }
              }
            }
            break;
          case "SAVE_NEW":
            {
              this.apiService.addData("numberscheme", this.entity).subscribe(
                (response) => this.handleNew(response),
                (error) => this.handleError(error, true)
              );
            }
            break;
        }
      } else {
        this.handleError("required_fields_must_be_entered");
      }
    }
  }

  handleError(error, commitError = false) {
    if (commitError) {
      if (this.numberSchemeCollectionView.sourceCollection) {
        if (this.numberSchemeCollectionView.sourceCollection.length === 0) {
          this.addNewRow();
        }
      }
      if (this.entity.number_scheme_lines) {
        if (this.entity.number_scheme_lines.length > 0) {
          for (let i = 0; i < this.entity.number_scheme_lines.length; i++) {
            if (this.entity.number_scheme_lines[i].rec_status) {
              // rec_status property'i varsa
              if (this.entity.number_scheme_lines[i].rec_status === "DELETE") {
                // mükerrer obje oluşmasın diye statusu DELETE olan kayıtlar siliniyor
                this.entity.number_scheme_lines.splice(i, 1);
                i = i - 1;
              }
            }
          }
        }
      }
    }
    this.alertShow = true;
    this.alertType = "error";
    this.klLoadingService.hide();
    this.alertMessage = this.util.alertErrorMessage(error);
    return of(null);
  }

  handleNew(response): void {
    this.alertShow = false;
    this.toastService.toastSuccess();
    this.formChange(false);
    this.klLoadingService.hide();
    this.clearForm();
  }

  handleResponse(response): void {
    this.klLoadingService.hide();
    this.toastService.toastSuccess();
    if (this.tabId) {
      this.formChange(false);
      this.explorerService.closeTab(this.tabId);
    } else if (this.modalId) {
      this.explorerService.closeModal(this);
    }
    if (this.listTabId) {
      this.explorerService.setRefreshEndProsseses(this.listTabId);
    }
  }

  unlockEntity() {
    this.apiService.unlockData("numberscheme", this.entity.id).subscribe();
  }

  initDefaults() {
    this.forkJoinStatus.subscribe(
      (status) => {
        if (status) {
          if (this.entity.user_role) {
            this.entity.user_role = this.numberSchemeUserRoleArray.find(
              (user_role) => this.entity.user_role.id === user_role.id
            );
          }
          if (this.entity.user) {
            this.entity.user = this.numberSchemeUserArray.find(
              (user) => this.entity.user.id === user.id
            );
          }
          this.klLoadingService.hide();
        } else {
          this.klLoadingService.hide();
        }
      },
      (error) => {
        this.klLoadingService.hide();
        this.handleError(error);
      }
    );
  }

  ngOnDestroy() {
    if (this.mode === "edit") {
      this.unlockEntity();
    }
  }
}
